// This session's endpoint activity read against a trace's turn windows —
// corroboration, and nothing stronger.
//
// The turn reading is the primary one: what the agent did, in the order it did
// it. This module supplies the other thing that was true at the same moment —
// what the agent's PROCESSES were doing — and it has to stay subordinate,
// because the join to a turn is by time and by time alone.
//
// Two joins, and conflating them is what makes the language dishonest. The
// SESSION join is an identity: an OS event carrying the session's
// notification_id was emitted by the agent's process tree, because the agent
// stamps that key on-host (see sessionScope in traceTurnClockFetch). The TURN
// join is a coincidence in time and cannot be strengthened — nothing here can
// say a turn RAN a process, and a window's upper bound is a REQUEST's capture
// time rather than the turn's, so the window is looser than it looks. What the
// pair supports is "this session's processes did this, during the window that
// turn fell in".
//
// This module owns the AMBIENT half of that reading: how much of each kind
// happened, which is a fact about a window and not about a turn. The other half
// — a name the window's rows also named — lives in traceEndpointMatch.js. It
// narrows the coincidence; it does not escape the paragraph above.
//
// The wire vocabulary lives in COMMAND_EVENT_TYPES (lib/apiClient.js), which is
// what the query filters on. What this module owns is the reading of it: how a
// wire type is named to a person, and in what order the kinds are listed.

// Wire event_type → how it reads in a sentence, and the glyph that stands for
// it in the expansion. A type absent from this table is NOT dropped — a
// newly-shipped event type shows under its wire name and a neutral dot, which
// is worse to look at than a label and far better than vanishing from a count
// the reader is using to judge what happened.
//
// Every glyph is a TEXT-presentation character (Emoji_Presentation=No), so it
// renders in the panel's own type rather than dropping an emoji sprite into a
// 10px line. They are literals rather than an icon set because the feed has no
// icon dependency and this is not the place to acquire one. The run of MARKS
// beside a boundary carries no glyphs — see windowMarks — because a glyph is a
// word and that run is a picture.
const KIND_LABELS = {
  ProcessStart: { one: "process", many: "processes", glyph: "⚙" },
  FileState: { one: "file change", many: "file changes", glyph: "✎" },
  FileDelete: { one: "file delete", many: "file deletes", glyph: "✕" },
  FileRename: { one: "file rename", many: "file renames", glyph: "⇄" },
  DnsQuery: { one: "DNS lookup", many: "DNS lookups", glyph: "⌕" },
  NetworkConnection: { one: "connection", many: "connections", glyph: "⇅" },
};

const KIND_ORDER = Object.keys(KIND_LABELS);

// An unrecognized wire type sorts LAST rather than first, which is what
// `indexOf` on its own would do with its -1.
function kindRank(kind) {
  const i = KIND_ORDER.indexOf(kind);
  return i < 0 ? KIND_ORDER.length : i;
}

export function kindLabel(kind, count = 1) {
  const entry = KIND_LABELS[kind];
  if (!entry) return String(kind || "");
  return count === 1 ? entry.one : entry.many;
}

export function kindGlyph(kind) {
  return KIND_LABELS[kind]?.glyph || "•";
}

/**
 * The window a turn's clock reading can be corroborated against, or null.
 *
 * @param reading  what useTurnClock's timeOf returns: { startTs, endTs, bound }
 * @returns { key, startTs, endTs } | null
 *
 * Three refusals, all of them the same refusal: only a rung bracketed on BOTH
 * sides names a span of time that anything can be looked up in.
 *
 * The "before" bound is the one worth stating outright. That rung is open on the
 * left — nothing was observed earlier — so every event the endpoint ever
 * produced falls inside it, and joining them would render the whole history
 * before the trace as a burst of activity in its first turn. joinEventsToClock
 * already drops those; this refuses to spend a query finding out.
 */
export function corroborationWindow(reading) {
  if (!reading || reading.bound !== "window") return null;
  const { startTs, endTs } = reading;
  if (!Number.isFinite(startTs) || !Number.isFinite(endTs) || endTs <= startTs) return null;
  return { key: `${startTs}:${endTs}`, startTs, endTs };
}

/**
 * The rungs a feed's rows fall in, each with the ROW EXTENT its turns cover.
 *
 * @param readings   one clock reading (or null) per row, in RENDER order
 * @param turnStarts one turn index (or null) per row, same order
 * @returns [{ key, startTs, endTs, window, from, to }] ascending by `from`
 *
 * The extent is measured against `turnStarts`, and that is the whole point.
 * `turnStarts` belongs to the FEED — every row's turn, known before any dating
 * happens — while `readings` fills in one turn at a time as a reader scrolls. A
 * rung names the turns it owns, so joining those two gives the same row span the
 * first time the rung is seen as it does once the whole trace is dated. Measured
 * off the dated rows instead, the span is the one SCROLLED SO FAR: it grows
 * under the reader, and everything anchored to it walks down the page.
 *
 * Rows are turn-ordered (buildSemanticFeed sorts on it), so a rung's turns pick
 * out a contiguous run of them. A rung with no rows at all is dropped: it dates
 * turns this feed does not render.
 */
function rungExtents(readings, turnStarts) {
  const turns = turnStarts || [];
  const byKey = new Map();
  for (const reading of readings || []) {
    if (!reading || !Number.isFinite(reading.endTs)) continue;
    if (!Number.isInteger(reading.fromTurn) || !Number.isInteger(reading.toTurn)) continue;
    const key = `${reading.startTs}:${reading.endTs}`;
    if (byKey.has(key)) continue;
    byKey.set(key, {
      key,
      startTs: reading.startTs,
      endTs: reading.endTs,
      fromTurn: reading.fromTurn,
      toTurn: reading.toTurn,
      window: corroborationWindow(reading),
      from: Infinity,
      to: -1,
    });
  }
  for (const rung of byKey.values()) {
    turns.forEach((turn, index) => {
      if (!Number.isInteger(turn) || turn < rung.fromTurn || turn > rung.toTurn) return;
      if (index < rung.from) rung.from = index;
      if (index > rung.to) rung.to = index;
    });
  }
  return [...byKey.values()].filter((r) => r.to >= r.from).sort((a, b) => a.from - b.from);
}

/**
 * A feed's rows partitioned into the turn windows they fall in.
 *
 * @param readings   one clock reading (or null) per row, in RENDER order
 * @param turnStarts one turn index (or null) per row, same order
 * @returns [{ window, from, to }] — inclusive row indices, in render order
 *
 * Two things come out of this and both are load-bearing.
 *
 * `from` is where the window's reading hangs: the boundary drawn before that row
 * is the instant the window opens, and windowBoundaries emits one there. Every
 * row of a stretch shares the window, so putting the reading on each of them
 * would repeat one set of events down the page and imply the events belonged to
 * each row in turn — the precise causal claim this reading cannot make.
 *
 * The EXTENT is what a name match needs: the rows a window covers are the whole
 * stretch, and a file edited three rows down sits inside exactly the same span
 * of time and is exactly as corroborable. It is the rung's own run of rows, so
 * an undated row inside it still counts — that row sat in the same span of time
 * whether or not anyone got around to asking the clock about it.
 */
export function windowStretches(readings, turnStarts) {
  return rungExtents(readings, turnStarts)
    .filter((r) => r.window)
    .map((r) => ({ window: r.window, from: r.from, to: r.to }));
}

/**
 * Where the rail draws a capture instant, and which window each one opens.
 *
 * @param readings   one clock reading (or null) per row, in RENDER order
 * @param turnStarts one turn index (or null) per row, same order
 * @returns { before: Map<rowIndex, { ts, window }>, closeIndex, closeTs }
 *
 * A boundary is one moment seen twice — the rung above closed at it, the rung
 * below opened at it. So each rung offers both of its instants at its own two
 * edges, and the pair that meet between two rungs collapse into the single tick
 * they always were. A rung open on the left offers only its ceiling, which is
 * the one instant it actually observed.
 *
 * Both edges come from the rung's row extent, never from how much of the trace
 * has been dated. That is what keeps a tick where it was first drawn: turns date
 * one at a time under a scrolling reader, so an instant placed at the last dated
 * row is placed at the reader's own position — it follows them down the page and
 * leaves nothing behind at any of the places it has been.
 *
 * The dedupe is on the RUNG, not on the formatted label and not on the instant
 * alone. Not the label, because two captures a few seconds apart are two
 * boundaries even though a minute-precision clock prints them the same, and
 * merging them would silently join two rungs. Not the instant alone, because a
 * boundary is the ONLY place a window's activity can be read: a rung that shared
 * its start with a degenerate neighbour would lose its boundary, and its events
 * would then have nowhere to hang.
 *
 * `window` is null for a boundary that opens nothing corroborable — the rung
 * open on the left, or one with no duration. The instant is still observed and
 * still reads; there is just no span to look anything up inside.
 */
export function windowBoundaries(readings, turnStarts) {
  const total = (readings || []).length;
  // Keyed by the row a tick is drawn BEFORE, which is why a rung's ceiling sits
  // at `to + 1`: `total` is a real slot too, the one under the last row.
  const slots = new Map();
  const put = (slot, ts, window) => {
    if (!Number.isFinite(ts)) return;
    const at = slots.get(slot);
    if (!at) {
      slots.set(slot, { ts, window: window || null });
      return;
    }
    // Where two rungs meet, the one BELOW owns the window: the instant opens its
    // span, and only closes the span above.
    if (!at.window && window) at.window = window;
  };
  for (const rung of rungExtents(readings, turnStarts)) {
    put(rung.from, rung.startTs, rung.window);
    put(rung.to + 1, rung.endTs, null);
  }
  const before = new Map();
  let closeIndex = -1;
  let closeTs = null;
  for (const [slot, at] of [...slots.entries()].sort((a, b) => a[0] - b[0])) {
    if (slot < total) before.set(slot, at);
    else {
      closeIndex = total - 1;
      closeTs = at.ts;
    }
  }
  return { before, closeIndex, closeTs };
}

/**
 * A window's events folded into per-kind rows: the expanded reading.
 *
 * The query projects id, time and type and nothing else, so a per-event list
 * would be hundreds of lines that each say only "something of this kind
 * happened". Per kind, with a count and the span it covered, is the whole of
 * what the evidence actually holds.
 */
export function corroborationGroups(events) {
  const byKind = new Map();
  for (const e of events || []) {
    if (!e || !e.kind) continue;
    if (!byKind.has(e.kind)) byKind.set(e.kind, { kind: e.kind, count: 0, firstTs: null, lastTs: null });
    const g = byKind.get(e.kind);
    g.count += 1;
    if (Number.isFinite(e.ts)) {
      if (g.firstTs == null || e.ts < g.firstTs) g.firstTs = e.ts;
      if (g.lastTs == null || e.ts > g.lastTs) g.lastTs = e.ts;
    }
  }
  return [...byKind.values()]
    .map((g) => ({ ...g, label: kindLabel(g.kind, g.count) }))
    .sort((a, b) => kindRank(a.kind) - kindRank(b.kind) || a.kind.localeCompare(b.kind));
}

/**
 * The collapsed one-liner: "4 processes · 2 file changes".
 *
 * Empty for no events, which is what lets the caller spend no pixels at all on
 * a window where nothing happened. It is not a line of the page — it is the
 * accessible name and hover label of the rail's dot run, which is where the
 * same reading lives for anyone who can see it.
 */
export function corroborationDigest(events) {
  return corroborationGroups(events)
    .map((g) => `${g.count} ${g.label}`)
    .join(" · ");
}

// How many marks a boundary's run can hold. Six is every kind KIND_LABELS
// names; past that the tail is a wire type shipped after this table, and the
// expansion enumerates every kind whatever the run shows.
const MAX_MARKS = 6;

/**
 * A count's magnitude, in the three steps a 2–4px mark can actually show apart.
 *
 * Three and not more: the run answers "was this window busy", and a reader
 * cannot rank five mark sizes by eye at that scale. The exact count is in the
 * expansion, which is the only place it is worth reading as a number.
 */
export function markWeight(count) {
  const n = Number(count) || 0;
  if (n >= 100) return 3;
  if (n >= 10) return 2;
  return 1;
}

/**
 * A window's events as the run of marks beside its boundary.
 *
 * @param events  the ambient projection, any order
 * @returns [{ kind, count, weight }] — one mark per kind, in reading order
 *
 * The order is corroborationGroups' order, and that is the point: the run and
 * the per-kind list a reader gets when they open the boundary are the same
 * sequence, so the third mark is the third line. Ordering the run by density
 * instead would make the picture and the list disagree about what comes first,
 * for no reading the sizes do not already carry.
 *
 * There is no time-slicing here and there should not be. When the marks were
 * threaded down the rows a window covered, cutting the window into one slice of
 * time per row was what gave each row something of its own to draw. A boundary
 * is a single instant on the page; a run laid along it has no vertical extent to
 * spend on time and no time axis to read against, so a position in the run would
 * imply an ordering in time that the run does not have.
 *
 * Empty for a window that held nothing, which is what lets the caller spend no
 * pixels at all on it.
 */
export function windowMarks(events) {
  return corroborationGroups(events)
    .slice(0, MAX_MARKS)
    .map((g) => ({ kind: g.kind, count: g.count, weight: markWeight(g.count) }));
}
